<template>
    <applicants-find
        v-slot="{
            data: applicant,
            promise: applicantsFind
        }"
        :params="{
            applicant: $route.params.applicant
        }"
        :include="[
            'address',
        ]"
        :find-on-created="false"
    >
        <applications-applicants-get
            v-slot="{
                promise: applicationsApplicantsGet
            }"
            :params="{
                application: $route.params.application,
                is_co_applicant: true
            }"
            :get-on-created="false"
        >
            <promise-all
                v-slot="{
                    has_loaded,
                    is_loading,
                    errors: find_errors,
                    execute: find
                }"
                :execute-on-created="true"
                :callstack="{
                    applicant: () => applicantsFind(),
                    coapplicants: () => applicationsApplicantsGet()
                }"
                @success="onFindSuccess"
            >
                <div>
                    <template v-if="!has_loaded">
                        <base-loading
                            :error="_.first(find_errors)"
                            :retry="find"
                            class="py-6"
                        />

                        <div
                            v-for="(find_error, index) in find_errors"
                            :key="index"
                        >
                            <error-modal
                                v-if="find_error && $error.is(find_error, { exclude: [400, 422] })"
                                :error="find_error"
                            />
                        </div>
                    </template>

                    <template v-else>
                        <applicants-update
                            v-slot="{
                                sid: applicant_sid,
                                promise: applicantsUpdate
                            }"
                            :params="{
                                applicant: $route.params.applicant
                            }"
                        >
                            <applications-applicants-update
                                v-slot="{
                                    sid: coapplicants_sid,
                                    promise: applicationsApplicantsUpdate
                                }"
                                :params="{
                                    application: $route.params.application
                                }"
                            >
                                <promise-all
                                    v-slot="{
                                        is_loading: is_saving,
                                        errors: update_errors,
                                        execute: update
                                    }"
                                    :execute-on-created="false"
                                    :callstack="{
                                        applicant: () => applicantsUpdate(forms.applicant),
                                        coapplicants: () => applicant.is_co_applicant
                                            ? _.resolve()
                                            : applicationsApplicantsUpdate(forms.coapplicants)
                                    }"
                                    @success="onUpdateSuccess"
                                    @error="onUpdateError"
                                >
                                    <base-form @submit="update">
                                        <div class="px-4 py-6 border-b sm:px-6">
                                            <form-section>
                                                <template slot="title">Basics</template>
                                                <template slot="description">
                                                    <strong>Your details.</strong> No abbreviations,
                                                    no nicknames - <strong>details as they appear on your ID.</strong>
                                                </template>

                                                <grid-layout>
                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="applicant_sid"
                                                            :is-required="true"
                                                            name="first_name"
                                                        >
                                                            <template slot="label">First Name</template>

                                                            <input-text
                                                                v-model="forms.applicant.first_name"
                                                                :sid="applicant_sid"
                                                                :disabled="is_saving"
                                                                name="first_name"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="applicant_sid"
                                                            :is-required="true"
                                                            name="last_name"
                                                        >
                                                            <template slot="label">Last Name</template>

                                                            <input-text
                                                                v-model="forms.applicant.last_name"
                                                                :sid="applicant_sid"
                                                                :disabled="is_saving"
                                                                name="last_name"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="applicant_sid"
                                                            :is-required="true"
                                                            name="date_of_birth"
                                                        >
                                                            <template slot="label">Date of Birth</template>

                                                            <input-date
                                                                v-model="forms.applicant.date_of_birth"
                                                                :sid="applicant_sid"
                                                                :disabled="is_saving"
                                                                name="date_of_birth"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>

                                                    <grid-layout-item class="md:w-1/2">
                                                        <form-item
                                                            :sid="applicant_sid"
                                                            name="telephone"
                                                        >
                                                            <template slot="label">Telephone</template>

                                                            <input-text
                                                                v-model="forms.applicant.telephone"
                                                                :sid="applicant_sid"
                                                                :disabled="is_saving"
                                                                type="tel"
                                                                name="telephone"
                                                                placeholder="07xxxxxxxxx"
                                                            />
                                                        </form-item>
                                                    </grid-layout-item>
                                                </grid-layout>
                                            </form-section>
                                        </div>

                                        <div class="px-4 pt-6 border-b sm:px-6">
                                            <form-section>
                                                <template slot="title">Address</template>
                                                <template slot="description">
                                                    Again, we require <strong>your address.</strong>
                                                </template>

                                                <form-items-address
                                                    v-model="forms.applicant.address"
                                                    :sid="applicant_sid"
                                                    :disabled="is_saving"
                                                />
                                            </form-section>
                                        </div>

                                        <div
                                            v-if="!applicant.is_co_applicant"
                                            class="px-4 py-6 border-b sm:px-6"
                                        >
                                            <form-section>
                                                <template slot="title">Co-Applicants</template>
                                                <template slot="description">
                                                    <div class="mb-3">If you’re applying with others, add them to your application.</div>

                                                    We’ll send them an email inviting them to apply as part of your group.
                                                </template>

                                                <div
                                                    v-for="(coapplicant, index) in forms.coapplicants"
                                                    :key="index"
                                                    class="relative mt-3 mb-6 border rounded"
                                                >
                                                    <header class="flex items-center px-4 py-3 text-xs border-b text-label bg-grey-lightest">
                                                        <div class="flex-grow">
                                                            {{ index + 1 | ordinal }} Co-Applicant
                                                        </div>

                                                        <button
                                                            type="button"
                                                            class="flex-none w-5 h-5 focus:outline-none"
                                                            @click.prevent="removeCoApplicant(coapplicant, index)"
                                                        >
                                                            <base-svg
                                                                name="icon-trash"
                                                                class="w-full h-full"
                                                            />
                                                        </button>
                                                    </header>

                                                    <div class="px-4 pt-6">
                                                        <grid-layout>
                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="coapplicants_sid"
                                                                    :is-required="true"
                                                                    :name="`coapplicants[${index}][first_name]`"
                                                                    :validator-name="`data.${index}.first_name`"
                                                                >
                                                                    <template slot="label">First Name</template>

                                                                    <input-text
                                                                        v-model="forms.coapplicants[index].first_name"
                                                                        :sid="coapplicants_sid"
                                                                        :disabled="is_saving"
                                                                        :name="`coapplicants[${index}][first_name]`"
                                                                        :validator-name="`data.${index}.first_name`"
                                                                    />
                                                                </form-item>
                                                            </grid-layout-item>

                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="coapplicants_sid"
                                                                    :is-required="true"
                                                                    :name="`coapplicants[${index}][last_name]`"
                                                                    :validator-name="`data.${index}.last_name`"
                                                                >
                                                                    <template slot="label">Last Name</template>

                                                                    <input-text
                                                                        v-model="forms.coapplicants[index].last_name"
                                                                        :sid="coapplicants_sid"
                                                                        :disabled="is_saving"
                                                                        :name="`coapplicants[${index}][last_name]`"
                                                                        :validator-name="`data.${index}.last_name`"
                                                                    />
                                                                </form-item>
                                                            </grid-layout-item>

                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="coapplicants_sid"
                                                                    :is-required="true"
                                                                    :name="`coapplicants[${index}][${forms.coapplicants[index].telephone ? 'telephone' : 'email'}]`"
                                                                    :validator-name="`data.${index}.${forms.coapplicants[index].telephone ? 'telephone' : 'email'}`"
                                                                >
                                                                    <template slot="label">
                                                                        Email or Mobile No
                                                                    </template>

                                                                    <input-text
                                                                        v-model.lazy="forms.coapplicants[index].email_or_telephone"
                                                                        :sid="coapplicants_sid"
                                                                        :disabled="is_saving"
                                                                        type="email"
                                                                        :name="`coapplicants[${index}][${forms.coapplicants[index].telephone ? 'telephone' : 'email'}]`"
                                                                        :validator-name="`data.${index}.${forms.coapplicants[index].telephone ? 'telephone' : 'email'}`"
                                                                    />

                                                                    <template slot="hint">
                                                                        If you enter a mobile no, we'll send an invite via SMS
                                                                    </template>
                                                                </form-item>
                                                            </grid-layout-item>
                                                        </grid-layout>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    class="btn btn-blue-lightest"
                                                    @click.prevent="addCoApplicant"
                                                >
                                                    <template v-if="forms.coapplicants.length == 0">
                                                        Add a co-applicant
                                                    </template>
                                                    <template v-else>
                                                        Add a {{ forms.coapplicants.length + 1 | ordinal }} co-applicant
                                                    </template>
                                                </button>
                                            </form-section>
                                        </div>

                                        <base-form-footer class="rounded-b">
                                            <base-button
                                                :is-loading="is_saving"
                                                class="w-full btn-blue md:w-auto"
                                            >
                                                Continue
                                            </base-button>
                                        </base-form-footer>

                                        <div
                                            v-for="(update_error, index) in update_errors"
                                            :key="index"
                                        >
                                            <error-modal
                                                v-if="update_error && $error.is(update_error, { exclude: [400, 422] })"
                                                :error="update_error"
                                            />
                                        </div>
                                    </base-form>
                                </promise-all>
                            </applications-applicants-update>
                        </applicants-update>
                    </template>
                </div>
            </promise-all>
        </applications-applicants-get>
    </applicants-find>
</template>

<script>
    import _ from 'lodash'
    import scroll from '../../../../support/scroll'
    import moment from 'moment'
    import HeadMixin from '../../../../mixins/head'
    import PreloadedMixin from '../../../../mixins/preloaded'
    import ApplicantsFind from '../../../../components/applicants-find'
    import ApplicantsUpdate from '../../../../components/applicants-update'
    import ApplicationsApplicantsGet from '../../../../components/applications-applicants-get'
    import ApplicationsApplicantsUpdate from '../../../../components/applications-applicants-update'
    import FormItemsAddress from '../../../../components/form-items-address'
    import PromiseAll from '../../../../components/promise-all'

    export default {
        mixins: [
            HeadMixin,
            PreloadedMixin
        ],
        components: {
            ApplicantsFind,
            ApplicantsUpdate,
            ApplicationsApplicantsGet,
            ApplicationsApplicantsUpdate,
            FormItemsAddress,
            PromiseAll
        },
        data () {
            return {
                sid: 'abc123',
                forms: {
                    applicant: {
                        first_name: '',
                        last_name: '',
                        date_of_birth: '',
                        telephone: '',
                        address: {
                            street: '',
                            locality: '',
                            village: '',
                            town: '',
                            county: '',
                            postcode: ''
                        }
                    },
                    coapplicants: []
                }
            }
        },
        watch: {
            'forms.coapplicants': {
                deep: true,
                handler (coapplicants) {
                    _.each(coapplicants, coapplicant => {
                        const isTelephone = !coapplicant.email_or_telephone.includes('@')

                        coapplicant.telephone = isTelephone ? coapplicant.email_or_telephone : null
                        coapplicant.email = isTelephone ? null : coapplicant.email_or_telephone
                    })
                }
            }
        },
        methods: {
            head () {
                const address = _.get(this.preloaded('application'), 'data.listing.property.address.name')

                return {
                    title: [
                        'Details',
                        `${address} Application`
                    ]
                }
            },
            addCoApplicant () {
                this.forms.coapplicants.push({
                    first_name: '',
                    last_name: '',
                    email_or_telephone: '',
                    email: null,
                    telephone: null
                })
            },
            removeCoApplicant (coapplicant, index) {
                this.forms.coapplicants = _.remove(this.forms.coapplicants, (coapplicant, j) => {
                    return index !== j
                })

                this.$validator.reset()
            },
            onUpdateSuccess () {
                this.$router.push({
                    name: 'applications.applicants.references.edit'
                })
            },
            onUpdateError () {
                scroll.toTop()
            },
            onFindSuccess ({ applicant: { data: applicant }, coapplicants: { data: coapplicants } }) {
                this.forms.applicant.first_name = applicant.first_name
                this.forms.applicant.last_name = applicant.last_name
                this.forms.applicant.date_of_birth = applicant.date_of_birth
                    ? moment(applicant.date_of_birth).format('DD/MM/YYYY')
                    : null
                this.forms.applicant.telephone = applicant.telephone

                if (applicant.address) {
                    this.forms.applicant.address.street = applicant.address.street
                    this.forms.applicant.address.locality = applicant.address.locality
                    this.forms.applicant.address.village = applicant.address.village
                    this.forms.applicant.address.town = applicant.address.town
                    this.forms.applicant.address.county = applicant.address.county
                    this.forms.applicant.address.postcode = applicant.address.postcode
                }

                _.each(coapplicants, coapplicant => {
                    this.forms.coapplicants.push({
                        uuid: coapplicant.uuid,
                        first_name: coapplicant.first_name,
                        last_name: coapplicant.last_name,
                        email_or_telephone: coapplicant.email || coapplicant.telephone
                    })
                })
            }
        }
    }
</script>
